// @ts-nocheck
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

/* Valtech Custom Code */
import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { categoryListProps } from "@store/Themes/common/props/category";
import { getSettingsCollection } from "@toolkit/Framework/Store/Context/ContextSelectors";

/* Hercules Custom Code - Start */
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import ArrowRight from "@insite/mobius/Icons/ArrowRight";
import getColor from "@insite/mobius/utilities/getColor";
import HeadingComponent, { HeadingProps } from "@store/Library/Components/Toolkit/Heading";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem, pxToRemArray } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { gridGapStyles } from "@toolkit/Themes/common/mixins";
/* Hercules Custom Code - End */

const StyledSpan = getStyledWrapper("span");
/* Valtech Custom Code */

const enum fields {
    showImages = "showImages",
}

interface Props extends WidgetProps, HasCategoryContext {}

export interface CategoryDetailSubCategoriesStyles {
    container?: GridContainerProps;
    subCategoryItem?: GridItemProps;
    innerContainer?: GridContainerProps;
    subCategoryImageItem?: GridItemProps;
    subCategoryImageClickable?: ClickablePresentationProps;
    subCategoryImage?: LazyImageProps;
    subCategoryNameLinkItem?: GridItemProps;
    subCategoryNameLink?: LinkPresentationProps;
    // Valtech Custom Code
    subCategoryImageWrapper?: InjectableCss;
    subCategoryTitle?: TypographyPresentationProps;
    subCategoryLink?: LinkPresentationProps;
    /* Hercules Custom Code - Start */
    wrapper?: InjectableCss;
    icon?: IconPresentationProps;
    iconItem?: GridItemProps;
    /* Hercules Custom Code - End */
}

export const categoryDetailSubCategoriesStyles: CategoryDetailSubCategoriesStyles = {
    /* Hercules Custom Code - Start */
    container: {
        gap: 20,
    },
    subCategoryItem: {
        width: [12, 12, 3, 3, 3],
    },
    /* Hercules Custom Code - End */
    subCategoryLink: categoryListProps.itemLink,
    subCategoryImageWrapper: categoryListProps.itemImageWrapper,
    /* Hercules Custom Code - Start */
    subCategoryImage: {
        css: css`
            ${x => x.theme.lazyImage.defaultProps?.css}
            height: 100px;
            width: 100px;
            ${breakpoint("md", "only")} {
                height: 75px;
                width: 75px;
            }
            ${breakpoint("sm", "down")} {
                height: 50px;
                width: 50px;
            }
        `,
    },
    /* Hercules Custom Code - End */
    subCategoryTitle: categoryListProps.itemTitle,
    /* Hercules Custom Code - Start - for some reason I have to add important otherwise the below flex-wrap isn't working */
    innerContainer: {
        gap: 0,
        offsetCss: css`
            ${gridGapStyles(pxToRemArray([0, null, 30, null, null]))}
            cursor: pointer;
            flex-wrap: initial !important;
            align-items: center;
            padding: ${pxToRem(16)};
            border: ${pxToRem(1)} solid ${getColor("common.borderLight")};
        `,
    },
    subCategoryImageItem: {
        width: [3, 3, 5, 5, 5],
        css: css`
            align-items: center;
            height: 100px;
            ${breakpoint("sm", "down")} {
                height: 50px;
            }
        `,
    },
    subCategoryNameLinkItem: {
        width: [8, 8, 6, 6, 6],
        css: css`
            padding-bottom: ${pxToRem(8)};
        `,
    },
    iconItem: {
        width: 1,
        css: css`
            padding-bottom: 0;
            ${breakpoint("md", "up")} {
                align-self: flex-end;
            }
        `,
    },
    icon: {
        src: ArrowRight,
        color: "text.link",
    },
    /* Hercules Custom Code - End */
};

const styles = categoryDetailSubCategoriesStyles;

const CategoryDetailSubCategories = ({ category, fields: { showImages } }: Props) => {
    if (!category || !category.subCategoryIds || category.subCategoryIds.length === 0) {
        return null;
    }

    return (
        <GridContainer {...styles.container}>
            {category.subCategoryIds.map(subCategoryId => (
                <GridItem key={subCategoryId} {...styles.subCategoryItem}>
                    <SubCategoryLink categoryId={subCategoryId} showImages={showImages} />
                </GridItem>
            ))}
        </GridContainer>
    );
};

const SubCategoryLinkView = ({
    category,
    showImages,
    // Valtech Custom Code
    defaultImagePath,
}: ReturnType<typeof mapStateToProps> & { showImages: boolean }) => {
    if (!category) {
        return null;
    }

    /* Valtech Custom Code */
    const imagePath = category.smallImagePath || defaultImagePath;

    /* Hercules Custom Code - Start */
    const handleClick = (link: string) => {
        window.location.href = link;
    };

    return (
        <GridContainer onClick={() => handleClick(category.path)} {...styles.innerContainer}>
            {showImages && (
                <GridItem {...styles.subCategoryImageItem}>
                    <LazyImage src={imagePath} altText={category.imageAltText} {...styles.subCategoryImage} />
                </GridItem>
            )}
            <GridItem {...styles.subCategoryNameLinkItem}>
                <Typography {...styles.subCategoryTitle}>{category.shortDescription}</Typography>
            </GridItem>
            <GridItem {...styles.iconItem}>
                <Icon {...styles.icon} />
            </GridItem>
        </GridContainer>
    );
    /* Hercules Custom Code - End */
    /* Valtech Custom Code */
};

const mapStateToProps = (state: ApplicationState, ownProps: { categoryId: string }) => ({
    category: getCategoryState(state, ownProps.categoryId).value,
    // Valtech Custom Code
    defaultImagePath: getSettingsCollection(state).extensionCatalogSettings?.notFoundCategorySmallImagePath,
});

const SubCategoryLink = connect(mapStateToProps)(SubCategoryLinkView);

const widgetModule: WidgetModule = {
    component: withCategory(CategoryDetailSubCategories),
    definition: {
        group: "Category Details",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.showImages,
                displayName: "Show Images",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
        allowedContexts: ["CategoryDetailsPage"],
    },
};

export default widgetModule;
